import { buyWithTon } from "../utils/purchaseHelpers";
import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from "../config";
import WebApp from "@twa-dev/sdk";
import { useTonConnectUI } from "@tonconnect/ui-react";
import { toast } from "react-toastify";
import { FaGem, FaArrowUp, FaStar, FaInfoCircle } from "react-icons/fa";
import { ImSpinner2 } from "react-icons/im";
import "react-toastify/dist/ReactToastify.css";
import { useTitle } from "../hooks/useTitle";
import ReactGA from "react-ga4";
import { motion, AnimatePresence } from "framer-motion";
import { useUser } from "../context/UserContext";
import LiquidGlass from "../components/LiquidGlass";
import haptic from "../utils/haptic";
import ConfirmationModal from "../components/ConfirmationModal";
import { checkPaymentStatusWithRetry, requestInvoiceLink } from "../utils/paymentHelpers";
import confetti from "canvas-confetti";
import Lottie from "lottie-react";
import minerAnimation from "../animations/miner.json";
import AdSense from "react-adsense";
import { calculateMinerGems } from "../utils/minerUtils";

const MAX_DURATION_MS = 8 * 60 * 60 * 1000; // 8 hours

const Miner = () => {
  useTitle("Miner Program");
  const { userData, APIKey, refreshUser } = useUser();
  const [minerData, setMinerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);
  const [claimLoading, setClaimLoading] = useState(false);
  const [nowTime, setNowTime] = useState(Date.now());
  
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [tonConnectUI] = useTonConnectUI();
  const [tonReceiverAddress, setTonReceiverAddress] = useState(null);
  
  const [upgradePlans, setUpgradePlans] = useState([]);
  const [expandedPlan, setExpandedPlan] = useState(null);
  const [levelRates, setLevelRates] = useState({});

  const fetchMinerStatus = async () => {
    try {
      const [minerRes, configRes] = await Promise.all([
        axios.get(`${API_URL}/api/miner`, {
          headers: { "x-api-key": APIKey },
          params: { initData: WebApp.initData }
        }),
        axios.get(`${API_URL}/api/miner/config`, { headers: { "x-api-key": APIKey } })
      ]);
      
      const configData = configRes.data || [];
      const plans = configData.filter(c => c.level > 1);
      setUpgradePlans(plans);
      
      const rates = {};
      configData.forEach(c => rates[c.level] = c.gemsPer8h);
      if (!rates[1]) rates[1] = 30; // fallback
      setLevelRates(rates);

      setMinerData(minerRes.data.miner);
      
      const last = new Date(minerRes.data.miner.lastClaimTime).getTime();
      const now = new Date(minerRes.data.currentTime).getTime();
      setNowTime(now);
      setErrorMsg(null);
    } catch (err) {
      console.error("Failed to fetch miner:", err);
      const backendError = err.response?.data?.error;
      const displayError = backendError || (err.response?.status === 404 ? "Route not found! Restart your server." : "Failed to connect to Miner.");
      setErrorMsg(displayError);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userData && APIKey) {
      fetchMinerStatus();
    }
  }, [userData, APIKey]);

  useEffect(() => {
    // TON Config
    axios.get(`${API_URL}/api/store/ton-config`, { headers: { "x-api-key": APIKey } })
      .then(res => setTonReceiverAddress(res.data.tonAddress))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!minerData) return;
    const interval = setInterval(() => {
      setNowTime(prev => prev + 1000);
    }, 1000);
    return () => clearInterval(interval);
  }, [minerData]);

  const handleClaim = async () => {
    haptic.tap();
    setClaimLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/miner/claim`, {}, {
        headers: { "x-api-key": APIKey },
        params: { initData: WebApp.initData }
      });
      if (res.data.success) {
        toast.success(`Claimed ${res.data.gemsAwarded} Gems!`);
        haptic.success();
        confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
        setMinerData(res.data.miner);
        setNowTime(new Date(res.data.currentTime).getTime());
        await refreshUser();
      }
    } catch (err) {
      const msg = err.response?.data?.error || "Error claiming gems";
      toast.error(msg);
      haptic.error();
    } finally {
      setClaimLoading(false);
    }
  };

  const handleUpgradeStars = async (plan) => {
    haptic.tap();
    setUpgradeLoading(true);
    try {
      const user = userData.user || userData;
      const { invoiceLink, orderId } = await requestInvoiceLink(
        user.userId || user.id,
        { gems: 0, tickets: 0, stars: plan.stars, _id: null, minerLevel: plan.level },
        APIKey
      );

      const cleanInvoiceLink = invoiceLink ? invoiceLink.replace(/"/g, '').trim() : "";
      WebApp.openInvoice(cleanInvoiceLink, async (status) => {
        if (status === "cancelled" || status === "failed") {
          toast.error("Payment was cancelled or failed.");
          haptic.error();
          return;
        }

        // Always wait for the webhook to confirm — never trust status === "paid" alone
        toast.info("Processing upgrade...");
        const isPaid = await checkPaymentStatusWithRetry(user.userId || user.id, orderId, APIKey, 10, 2000);

        if (isPaid) {
          haptic.success();
          confetti();
          setUpgradeModalOpen(false);
          await fetchMinerStatus();
          toast.success("Miner upgraded successfully!");
        } else {
          toast.error("Could not confirm payment. Please refresh.");
          haptic.error();
        }
      });
    } catch (err) {
      console.error("Stars Upgrade Error:", err);
      toast.error(err?.response?.data?.error || err.message || "Stars Upgrade failed.");
    } finally {
      setUpgradeLoading(false);
    }
  };

  const handleUpgradeTon = async (plan) => {
    haptic.tap();
    if (!tonConnectUI.connected) {
      toast.info("Connect TON wallet in Profile first!");
      tonConnectUI.openModal();
      return;
    }
    if (!tonReceiverAddress) {
      toast.error("TON config missing.");
      return;
    }
    
    setUpgradeLoading(true);
    try {
      const user = userData.user || userData;
      const result = await buyWithTon({ minerLevel: plan.level }, user.userId || user.id, APIKey, tonConnectUI);
      const verifyRes = { data: { success: result.state === "fulfilled" } };
      if (!verifyRes.data.success) {
        toast.info(`Upgrade pending. Resume in Profile → Purchases. Order: ${result.orderId}. Do not pay again.`);
        return;
      }

      if (verifyRes.data.success) {
        toast.success("Miner upgraded via GRAM!");
        haptic.success();
        confetti();
        setUpgradeModalOpen(false);
        await fetchMinerStatus();
      } else {
        toast.error("Blockchain verification failed.");
      }
    } catch (err) {
      let emsg = err?.message?.includes("Reject") ? "Transaction rejected." : "GRAM Payment failed.";
      toast.error(emsg);
    } finally {
      setUpgradeLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-slate-50 dark:bg-slate-900">
        <ImSpinner2 className="animate-spin text-4xl text-purple-500" />
      </div>
    );
  }

  if (errorMsg) {
    return (
      <div className="flex flex-col gap-4 justify-center items-center h-screen bg-slate-50 dark:bg-slate-900 px-4 text-center">
        <div className="text-red-500 text-xl font-bold">Error</div>
        <div className="text-slate-600 dark:text-slate-300">{errorMsg}</div>
        <button onClick={fetchMinerStatus} className="mt-4 px-6 py-2 bg-indigo-500 text-white rounded-lg">Retry</button>
      </div>
    );
  }

  if (!minerData) return null;

  const level = minerData.level || 1;
  const maxGems = levelRates[level] || 30;
  
  const { currentGems, progressPct, isFull } = calculateMinerGems(minerData, nowTime, levelRates);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="bg-slate-50 dark:bg-slate-900 min-h-screen pt-20 pb-24 px-4 text-slate-900 dark:text-white transition-colors duration-300 relative overflow-hidden"
    >
      {upgradeLoading && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <ImSpinner2 className="animate-spin text-white text-4xl" />
        </div>
      )}

      {/* Hero Banner */}
      <div className="bg-gradient-to-br from-amber-400 via-orange-500 to-red-500 rounded-3xl p-6 shadow-[0_10px_40px_-10px_rgba(249,115,22,0.5)] mb-6 relative overflow-hidden border border-white/20">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 mix-blend-overlay"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-amber-300/30 rounded-full blur-2xl translate-y-1/2 -translate-x-1/4 mix-blend-overlay"></div>
        
        <div className="relative z-10">
          <h1 className="text-3xl font-black text-white flex items-center gap-2 drop-shadow-md tracking-tight">
            <FaGem className="text-amber-100 drop-shadow-sm" /> Gem Miner 
            <span className={`text-xs px-2.5 py-1 rounded-full font-extrabold uppercase tracking-widest shadow-inner ${level === 1 ? 'bg-amber-700/40 text-amber-100' : 'bg-gradient-to-r from-yellow-300 to-yellow-500 text-yellow-900 shadow-yellow-500/50'}`}>
              Lv. {level}
            </span>
          </h1>
          <p className="text-white/90 text-sm mt-2 font-medium bg-black/10 inline-block px-3 py-1.5 rounded-lg backdrop-blur-sm border border-white/10">
            Produces <span className="font-bold text-amber-200">{levelRates[level] || 30} gems</span> every 8 hours
          </p>
          
          {level > 1 && minerData.upgradeExpiry && (
            <p className="text-amber-100 text-xs font-bold mt-3 flex items-center gap-1.5 bg-black/20 px-3 py-2 rounded-lg border border-amber-300/20 backdrop-blur-sm w-fit">
              <FaInfoCircle />
              Active until: {new Date(minerData.upgradeExpiry).toLocaleDateString()} {new Date(minerData.upgradeExpiry).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
            </p>
          )}
        </div>
      </div>

      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => { haptic.tap(); setUpgradeModalOpen(true); }}
        className={`w-full relative overflow-hidden rounded-2xl p-5 text-left shadow-xl transition-all mb-4 group ${
          level === 1 
            ? "bg-gradient-to-r from-rose-500 via-orange-500 to-amber-500 shadow-orange-500/30 hover:shadow-orange-500/50 border border-orange-400/50" 
            : "bg-gradient-to-r from-amber-500 to-orange-600 shadow-orange-500/30 hover:shadow-orange-500/50 border border-amber-400/30"
        }`}
      >
        {/* Animated sheen effect */}
        <div className="absolute inset-0 bg-white/20 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
        <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>

        <div className="relative z-10 flex flex-col gap-3">
          <div className="flex-1">
            {level === 1 && (
              <div className="inline-flex items-center gap-1.5 bg-red-600/80 text-white px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider mb-2 backdrop-blur-sm shadow-sm animate-pulse">
                <span>⚠️</span> Losing potential gems
              </div>
            )}
            <h3 className="font-black text-white flex items-center gap-2 text-lg drop-shadow-md">
              <FaArrowUp className="animate-bounce text-amber-200" /> {level === 1 ? "Upgrade Your Miner" : "Boost Mining Speed"}
            </h3>
            <p className="text-xs text-orange-50 font-medium mt-1.5 drop-shadow-sm opacity-90 leading-relaxed">
              {level === 1 
                ? "Your basic miner is slow. Upgrade now to stop missing out on thousands of gems daily!" 
                : "Multiply your daily earnings instantly with a premium miner."}
            </p>
          </div>
          <div className="relative z-10 bg-white text-orange-600 px-4 py-3 rounded-xl text-sm text-center font-black shadow-lg shadow-black/10 uppercase tracking-widest group-hover:scale-[1.02] transition-transform w-full">
            View Plans
          </div>
        </div>
      </motion.button>

      <LiquidGlass className="rounded-3xl p-6 border border-slate-200/60 dark:border-slate-700/50 mb-6 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:shadow-2xl relative overflow-hidden">
        {/* Dynamic Glowing Orb behind miner */}
        <div className={`absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 rounded-full blur-3xl transition-all duration-1000 ${isFull ? 'bg-amber-400/40 scale-125' : 'bg-orange-500/20'}`}></div>

        <div className="w-48 h-48 mx-auto -mt-2 mb-2 flex items-center justify-center relative drop-shadow-xl z-10">
          <Lottie 
            animationData={minerAnimation} 
            loop={!isFull} 
            autoplay={!isFull}
            className={`w-full h-full object-contain transition-transform duration-500 ${isFull ? 'scale-110 drop-shadow-[0_0_15px_rgba(251,191,36,0.6)]' : ''}`}
          />
        </div>

        <div className="relative z-10">
          <h2 className="text-4xl font-black text-slate-800 dark:text-white mb-4 tracking-tight drop-shadow-sm flex items-end justify-center gap-1">
            {isFull ? maxGems : currentGems.toFixed(1)} <span className="text-lg text-slate-400 dark:text-slate-500 font-bold mb-1">/ {maxGems}</span>
          </h2>
          
          <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-4 mb-3 overflow-hidden shadow-inner border border-slate-200 dark:border-slate-700 relative">
            <motion.div 
              className={`h-full rounded-full relative overflow-hidden ${isFull ? 'bg-gradient-to-r from-amber-400 to-yellow-400' : 'bg-gradient-to-r from-orange-400 to-amber-500'}`}
              style={{ width: `${progressPct}%` }}
              transition={{ duration: 0.5 }}
            >
              {/* Glossy overlay */}
              <div className="absolute inset-0 bg-white/20 h-1/2 rounded-full"></div>
            </motion.div>
          </div>
          
          <div className="flex justify-between items-center mb-6 px-1">
            <p className={`text-xs font-bold uppercase tracking-wider ${isFull ? 'text-amber-500 dark:text-amber-400 animate-pulse' : 'text-slate-400 dark:text-slate-500'}`}>
              {isFull ? "MINER FULL — CLAIM NOW" : "MINING IN PROGRESS..."}
            </p>
            {!isFull && (
              <p className="text-xs font-bold text-orange-500 dark:text-orange-400 bg-orange-100 dark:bg-orange-900/30 px-2 py-1 rounded-md">
                {(() => {
                  const msRemaining = MAX_DURATION_MS - (nowTime - new Date(minerData.lastClaimTime).getTime());
                  if (msRemaining <= 0) return "0h 0m";
                  const hrs = Math.floor(msRemaining / (1000 * 60 * 60));
                  const mins = Math.floor((msRemaining % (1000 * 60 * 60)) / (1000 * 60));
                  return `${hrs}h ${mins}m left`;
                })()}
              </p>
            )}
          </div>

          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={handleClaim}
            disabled={claimLoading || !isFull}
            className={`w-full py-4 rounded-2xl font-black text-lg shadow-lg flex items-center justify-center gap-2 transition-all relative overflow-hidden group ${
              isFull 
                ? "bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-300 hover:to-orange-400 text-white shadow-orange-500/40 ring-2 ring-amber-400/50 ring-offset-2 ring-offset-slate-50 dark:ring-offset-slate-900"
                : "bg-slate-200 dark:bg-slate-800 text-slate-400 dark:text-slate-600 cursor-not-allowed border border-slate-300 dark:border-slate-700"
            }`}
          >
            {isFull && <div className="absolute inset-0 bg-white/20 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>}
            {claimLoading ? <ImSpinner2 className="animate-spin text-xl" /> : (
              <>
                <FaGem className={isFull ? "drop-shadow-md" : ""} /> Claim Gems
              </>
            )}
          </motion.button>
        </div>
      </LiquidGlass>


      {/* Ad Banner — below miner card, above upgrade */}
      <div className="mt-2 mb-4 mx-auto">
        <AdSense.Google
          client="ca-pub-4252052358641832"
          slot="4385377268"
          style={{ display: "block" }}
          format="auto"
          responsive="true"
          layoutKey="-gw-1+2a-9x+5c"
        />
      </div>

      {/* Upgrade Modal */}
      <AnimatePresence>
        {upgradeModalOpen && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-end justify-center">
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="bg-white dark:bg-slate-800 w-full rounded-t-3xl p-6 pb-24 shadow-2xl max-w-md max-h-[95vh] flex flex-col"
            >
              <div className="flex justify-between items-center mb-6 shrink-0">
                <h3 className="text-xl font-bold text-slate-900 dark:text-white">Upgrade Miner</h3>
                <button onClick={() => setUpgradeModalOpen(false)} className="text-slate-400 hover:text-slate-600 text-2xl font-light">&times;</button>
              </div>
              <div className="shrink-0">
                <p className="text-xs text-red-500 dark:text-red-400 mb-5 flex items-start gap-2 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded-xl border border-red-200 dark:border-red-800/40">
                  <span className="mt-0.5 flex-shrink-0">⚠️</span>
                  <span>All purchases are <strong>non-refundable</strong>. Please confirm your selection before purchasing.</span>
                </p>
              </div>
              
              <div className="space-y-3 overflow-y-auto pb-6 flex-1 min-h-0">
                {upgradePlans.map((plan, i) => {
                  const isExpanded = expandedPlan === plan.level;
                  return (
                    <motion.div 
                      key={i} 
                      whileHover={{ scale: 1.02 }}
                      onClick={() => setExpandedPlan(isExpanded ? null : plan.level)}
                      className={`relative overflow-hidden rounded-2xl p-5 flex flex-col gap-4 border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-800/80 backdrop-blur-xl transition-all cursor-pointer ${isExpanded ? "shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:shadow-2xl" : "shadow-sm hover:shadow-md"}`}
                    >
                      {/* Decorative gradient blob */}
                      <div className="absolute -top-12 -right-12 w-32 h-32 bg-gradient-to-br from-purple-500/20 to-indigo-500/20 dark:from-purple-500/30 dark:to-indigo-500/30 rounded-full blur-2xl pointer-events-none" />
                      
                      {plan.level === 3 && (
                        <div className="absolute top-0 right-0 bg-gradient-to-r from-rose-500 to-orange-500 text-white text-[10px] font-black uppercase px-3 py-1 rounded-bl-2xl z-20 shadow-sm">
                          Popular
                        </div>
                      )}
                      
                      <div className="flex justify-between items-center relative z-10">
                        <div className="flex flex-col">
                          <span className="font-extrabold text-xl bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-purple-600 dark:from-indigo-300 dark:to-purple-300">
                            {plan.title}
                          </span>
                          <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 mt-0.5">
                            Total: <span className="text-amber-500 font-bold">{(plan.gemsPer8h || levelRates[plan.level] || 0) * 3 * 30} gems</span> / 30 days
                          </span>
                        </div>
                        <span className="text-xs font-bold bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-3 py-1.5 rounded-full shadow-md shadow-indigo-500/30">
                          {plan.gemsPer8h || levelRates[plan.level] || 0} gems / 8h
                        </span>
                      </div>
                      
                      <AnimatePresence>
                        {isExpanded ? (
                          <motion.div 
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="flex gap-3 w-full relative z-10 mt-1"
                          >
                            <motion.button 
                              whileTap={{ scale: 0.95 }}
                              onClick={(e) => { e.stopPropagation(); handleUpgradeStars(plan); }}
                              className="flex-1 bg-gradient-to-b from-amber-400 to-orange-500 hover:from-amber-300 hover:to-orange-400 shadow-lg shadow-orange-500/30 border border-orange-400/50 rounded-xl py-3 flex flex-col items-center justify-center transition-colors relative overflow-hidden group"
                            >
                              <div className="absolute inset-0 bg-white/20 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-500" />
                              <span className="text-xl font-black text-white drop-shadow-md">{plan.stars}</span>
                              <span className="text-xs text-orange-100 font-bold flex items-center gap-1 uppercase tracking-wider mt-0.5"><FaStar className="drop-shadow-sm" /> Stars</span>
                            </motion.button>
                            <motion.button 
                              whileTap={{ scale: 0.95 }}
                              onClick={(e) => { e.stopPropagation(); handleUpgradeTon(plan); }}
                              className="flex-1 bg-gradient-to-b from-[#33aadd] to-[#0077b3] hover:from-[#44bbff] hover:to-[#0088cc] shadow-lg shadow-[#0088cc]/30 border border-[#006699]/50 rounded-xl py-3 flex flex-col items-center justify-center transition-colors relative overflow-hidden group"
                            >
                              <div className="absolute inset-0 bg-white/20 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-500" />
                              <span className="text-xl font-black text-white drop-shadow-md">{plan.tonPrice}</span>
                              <span className="text-xs text-blue-100 font-bold uppercase tracking-wider mt-0.5">GRAM</span>
                            </motion.button>
                          </motion.div>
                        ) : (
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="relative z-10 mt-2"
                          >
                            <div className="w-full bg-slate-100 dark:bg-slate-700/50 text-slate-500 dark:text-slate-300 py-3 rounded-xl font-bold text-sm text-center border border-slate-200 dark:border-slate-600 transition-colors hover:bg-slate-200 dark:hover:bg-slate-700">
                              Upgrade
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}
              </div>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-4 flex items-center gap-2 bg-slate-100 dark:bg-slate-700/50 p-3 rounded-xl border border-slate-200 dark:border-slate-600">
                <FaInfoCircle className="text-indigo-500 flex-shrink-0" /> All upgrades last exactly 30 Days. Time stacks if you buy the same Level again.
              </p>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default Miner;
